import { ArrowUpRight, Brain, Search, Sparkles } from 'lucide-react';
import {
  EmptyState,
  inputClassName,
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

function formatConfidence(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'N/A';
  }

  return `${Math.round(value * 100)}%`;
}

function formatUpdatedAt(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatSourceType(sourceType) {
  switch (sourceType) {
    case 'calendar_event':
      return 'Calendar';
    case 'list_item':
      return 'List item';
    case 'queue':
      return 'Queue';
    case 'task':
      return 'Task';
    default:
      return 'Memory';
  }
}

function totalSearched(result) {
  if (!result?.searched) {
    return 0;
  }

  return Object.values(result.searched).reduce(
    (total, value) => total + (typeof value === 'number' ? value : 0),
    0
  );
}

function EvidenceCard({ evidence, onOpenNavigationTarget }) {
  const updatedAt = formatUpdatedAt(evidence?.updatedAt);

  return (
    <article className="rounded-[24px] border border-app-border/80 bg-app-surface/95 p-5 shadow-subtle">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone="accent">{formatSourceType(evidence?.sourceType)}</StatusPill>
          <StatusPill tone="neutral">Score {Math.round(evidence?.score || 0)}</StatusPill>
          {updatedAt ? <StatusPill tone="neutral">{updatedAt}</StatusPill> : null}
        </div>
        {evidence?.navigationTarget ? (
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
            type="button"
            onClick={() => onOpenNavigationTarget(evidence.navigationTarget)}
          >
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            <span>Open destination</span>
          </button>
        ) : null}
      </div>

      <div className="mt-4">
        <p className="m-0 text-base font-semibold text-app-ink">
          {evidence?.title || 'Untitled evidence'}
        </p>
        {evidence?.snippet ? (
          <p className="mt-2 text-sm leading-7 text-app-muted">{evidence.snippet}</p>
        ) : null}
      </div>
    </article>
  );
}

function ContextSearchView({
  question,
  searchResult,
  searchStatus,
  isSearching,
  onQuestionChange,
  onSubmitSearch,
  onOpenNavigationTarget,
}) {
  const evidenceCount = searchResult?.evidence?.length || 0;
  const searchedCount = totalSearched(searchResult);
  const confidenceValue = formatConfidence(searchResult?.confidence);
  const statusTone =
    searchStatus.type === 'warning'
      ? 'warning'
      : searchStatus.type === 'loading'
        ? 'loading'
        : searchStatus.type || 'neutral';

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <PageHero
        eyebrow="Context Search"
        title="Ask the workspace"
        lede="Search family context across memories, tasks, calendar events, list items, and recent queue history from one screen."
        bearyMessage={searchStatus.type === 'loading' ? 'Beary is searching family context.' : undefined}
        bearyPreset="loading"
        action={<StatusPill tone={statusTone}>{searchStatus.message}</StatusPill>}
      />

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        <MetricCard label="Evidence" value={evidenceCount} icon={Search} accentClassName="text-app-ink" />
        <MetricCard label="Records searched" value={searchedCount} icon={Brain} accentClassName="text-blue-700" />
        <MetricCard label="Confidence" value={confidenceValue} icon={Sparkles} accentClassName="text-emerald-700" />
      </div>

      <div className="mt-8 grid gap-8 xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="space-y-8">
          <SectionCard>
            <SectionTitle
              title="Search question"
              copy="Submit a full question instead of keywords so the backend can synthesize a direct answer from the retrieved evidence."
            />

            <form className="mt-6 space-y-5" onSubmit={onSubmitSearch}>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="context-search-question">
                  Question
                </label>
                <textarea
                  id="context-search-question"
                  rows="6"
                  value={question}
                  onChange={(event) => onQuestionChange(event.target.value)}
                  placeholder="What time is pickup on Thursday?"
                  className={inputClassName}
                />
              </div>

              <button
                className="inline-flex items-center justify-center rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong disabled:cursor-not-allowed disabled:opacity-60"
                type="submit"
                disabled={isSearching || !question.trim()}
              >
                {isSearching ? 'Searching...' : 'Search context'}
              </button>
            </form>
          </SectionCard>

          <SectionCard>
            <SectionTitle
              title="Search coverage"
              copy="The backend automatically includes active family data when the signed-in user belongs to a family."
            />

            <div className="mt-5 space-y-3 text-sm leading-7 text-app-muted">
              <p className="m-0">Memories, personal and family tasks, calendar events, list items, and recent queue context are searched together.</p>
              <p className="m-0">When the answer is weak, the search still returns evidence so you can inspect the underlying context directly.</p>
            </div>
          </SectionCard>
        </aside>

        <div className="space-y-8">
          <SectionCard>
            <SectionTitle
              title="Answer"
              copy={
                searchResult?.question
                  ? `Question: ${searchResult.question}`
                  : 'Submit a question to generate an answer backed by workspace evidence.'
              }
              action={
                searchResult ? (
                  <StatusPill tone={searchResult.answer ? 'success' : 'warning'}>
                    {searchResult.answer ? confidenceValue : 'Evidence only'}
                  </StatusPill>
                ) : null
              }
            />

            {searchResult ? (
              <div className="mt-6 space-y-4">
                <div className="rounded-[24px] border border-app-border/70 bg-app-shell p-5">
                  <p className="m-0 text-base leading-8 text-app-ink">
                    {searchResult.answer || 'No strong answer found from the retrieved context.'}
                  </p>
                </div>

                {searchResult.notes ? (
                  <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    {searchResult.notes}
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  <StatusPill tone="neutral">Memories {searchResult.searched.memories}</StatusPill>
                  <StatusPill tone="neutral">Calendar {searchResult.searched.calendarEvents}</StatusPill>
                  <StatusPill tone="neutral">Tasks {searchResult.searched.tasks}</StatusPill>
                  <StatusPill tone="neutral">List items {searchResult.searched.listItems}</StatusPill>
                  <StatusPill tone="neutral">Queues {searchResult.searched.queues}</StatusPill>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <EmptyState
                  title="No search yet."
                  copy="Start with a concrete question like pickup time, next task due, or what was decided in the latest queue result."
                />
              </div>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitle
              title="Evidence"
              copy="Review the strongest matching records and jump into the related workspace screen when a navigation target is available."
              action={searchResult ? <StatusPill tone="neutral">{evidenceCount}</StatusPill> : null}
            />

            {searchResult?.evidence?.length ? (
              <div className="mt-6 grid gap-4">
                {searchResult.evidence.map((evidence) => (
                  <EvidenceCard
                    key={`${evidence.id}-${evidence.sourceType}`}
                    evidence={evidence}
                    onOpenNavigationTarget={onOpenNavigationTarget}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-6">
                <EmptyState
                  title={searchResult ? 'No evidence returned.' : 'Evidence will appear here.'}
                  copy={
                    searchResult
                      ? 'Try a more specific question or mention a person, date, or task to narrow the search.'
                      : 'After you run a search, the strongest matching records will show up here.'
                  }
                />
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </section>
  );
}

export default ContextSearchView;
